import * as turf from '@turf/turf';
import { Outline } from '../model/Outline';
import Field from '../model/Field';

export function goToFields(map, fields, weatherStations) {
  if (!map) {
    return;
  }

  let bounds;
  if (fields) {
    bounds = getFieldBounds(fields);
  }

  if (bounds) {
    map.fitBounds(bounds);
  }
  if (!bounds && weatherStations) {
    goToWeatherStations(map, weatherStations);
  }
}

export function goToField(map, field, offsetBottom, disableZoom) {
  const target = new Map();
  target.set(field.fieldId, field);

  const mapBounds = map.getBounds();

  const ne = mapBounds.getNorthEast();
  const sw = mapBounds.getSouthWest();
  const n = ne.lat(),
    s = sw.lat();

  const height = n > s ? n - s : s - n;

  const fieldBounds = getFieldBounds(target);

  if (fieldBounds) {
    const c = fieldBounds.getCenter();
    let lat = c.lat();
    const lng = c.lng();

    if (offsetBottom) {
      lat -= height / 5;
    }

    const target = new google.maps.LatLng(lat, lng);

    map.setCenter(target);

    if (!disableZoom && map.getZoom() < 14) {
      map.setZoom(14);
    }
  }
}

export function getFieldBounds(fields) {
  if (!fields) {
    return undefined;
  }

  const bounds = new google.maps.LatLngBounds();
  const points = [];

  for (const entry of fields) {
    const field = entry[1];
    const { north, south, east, west } = field.bounds;

    points.push(new google.maps.LatLng(north, west));
    points.push(new google.maps.LatLng(south, east));
  }

  if (points.length <= 0) {
    return;
  }

  for (let i = 0; i < points.length; i++) {
    bounds.extend(points[i]);
  }

  return bounds;
}

export function goToWeatherStations(map, stations) {
  if (!stations) {
    return;
  }

  const bounds = new google.maps.LatLngBounds();
  const points = [];

  for (const station of stations) {
    const loc = station.location;
    if (loc) {
      points.push(new google.maps.LatLng(loc.latitude, loc.longitude));
    }
  }

  if (points.length <= 0) {
    return;
  }

  for (let i = 0; i < points.length; i++) {
    bounds.extend(points[i]);
  }
  map.fitBounds(bounds);
  map.setZoom(map.getZoom() - 1);
}

export function fitToOutlines(outlines, googleMap) {
  const bounds = new google.maps.LatLngBounds();
  outlines.forEach((entry) => {
    entry.coordinates.forEach((c) => {
      bounds.extend(c);
    });
  });
  googleMap.fitBounds(bounds);

  if (outlines.length > 1) {
    googleMap.setZoom(googleMap.getZoom() + 1);
  }
}

export const fitMapToBounds = (googleMap, bounds) => {
  bounds = new google.maps.LatLngBounds(
    new google.maps.LatLng(bounds.south, bounds.west),
    new google.maps.LatLng(bounds.north, bounds.east)
  );

  // Prevents the map from zooming in too much
  googleMap.setOptions({ maxZoom: 18 });
  googleMap.fitBounds(bounds, { bottom: 5, left: 5, right: 5, top: 5 });
  googleMap.setOptions({ maxZoom: null });
};

export const getFieldCenter = (field) => {
  const coords = field.polygon.coordinates;
  const p = coords[0];

  const first = turf.flip(turf.point(p[0])),
    last = turf.flip(turf.point(p[p.length - 1]));

  if (!turf.booleanEqual(first, last)) {
    coords[0].push(turf.getCoord(turf.flip(first)));
  }

  const poly = turf.polygon(coords);
  const center = turf.flip(turf.centerOfMass(poly));
  const position = center.geometry.coordinates;
  return { lat: position[0], lng: position[1] };
};

export const getLatLngValues = (latLng) => {
  const lat = latLng && (typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat);
  const lng = latLng && (typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng);

  return { lat, lng };
};

export const mergeBounds = (boundsA, boundsB) => {
  if (!boundsA || !boundsB) return;
  return {
    north: Math.max(boundsA.north, boundsB.north),
    south: Math.min(boundsA.south, boundsB.south),
    east: Math.max(boundsA.east, boundsB.east),
    west: Math.min(boundsA.west, boundsB.west),
  };
};

export const getRect = (map, mapDOM, points) => {
  if (map && points.length === 2) {
    const first = getScreenXY(map, points[0]);
    const last = getScreenXY(map, points[1]);

    // First, we assume that the selection direction is from Left to Right, from Top to Bottom.
    let topLeft = first,
      bottomRight = last;

    // Check if the direction is in fact Right to Left, if so, flip X-direction
    const rightToLeftSelection = first.x > last.x;
    if (rightToLeftSelection) {
      topLeft = new google.maps.Point(last.x, first.y);
      bottomRight = new google.maps.Point(first.x, last.y);
    }

    // Check if the direction is in fact Bottom to Top, if so, flip Y-direction
    const bottomToTopSelection = first.y > last.y;
    if (bottomToTopSelection) {
      const tmpY = topLeft.y;
      topLeft.y = bottomRight.y;
      bottomRight.y = tmpY;
    }

    return { topLeft, bottomRight };
  }
};

export const getScreenXY = (map, latLng) => {
  const numTiles = 1 << map.getZoom();
  const projection = map.getProjection();
  const worldCoordinate = projection.fromLatLngToPoint(latLng);
  const pixelCoordinate = new google.maps.Point(
    worldCoordinate.x * numTiles,
    worldCoordinate.y * numTiles
  );

  const topLeft = new google.maps.LatLng(
    map.getBounds().getNorthEast().lat(),
    map.getBounds().getSouthWest().lng()
  );

  const topLeftWorldCoordinate = projection.fromLatLngToPoint(topLeft);
  const topLeftPixelCoordinate = new google.maps.Point(
    topLeftWorldCoordinate.x * numTiles,
    topLeftWorldCoordinate.y * numTiles
  );

  return new google.maps.Point(
    pixelCoordinate.x - topLeftPixelCoordinate.x,
    pixelCoordinate.y - topLeftPixelCoordinate.y
  );
};

export const getClickedFields = (fields, event) => {
  const point = turf.point([event.latLng.lng(), event.latLng.lat()]);

  return [...fields.values()].filter((f) => {
    const borderPoints = f.polygon.coordinates[0];
    const border = turf.polygon([borderPoints]);
    return turf.booleanContains(border, point);
  });
};

export function fieldToTurfPolygon(field: Field) {
  return turf.polygon(field.polygon.coordinates);
}

/**
 * Filters out outlines whose centers are closer than 50 m to an already existing field.
 */
export function getFilteredOutlinesSoft(
  outlines,
  existingFields,
  progressCallback = (progress, progressMax, skipped) => {}
) {
  let progress = 0,
    skipped = 0,
    progressMax = outlines.length;

  try {
    const result = [];
    // Figure out which outlines represents fields which are already added
    const fieldPolygons = existingFields.map(fieldToTurfPolygon);
    const fieldCenters = fieldPolygons.map((p) => turf.centerOfMass(p));

    outer: for (const outline of outlines) {
      progressCallback(progress, progressMax, skipped);
      progress++;

      const p = turf.polygon(outline.coordinates);
      const pCenter = turf.centerOfMass(p);

      for (const fCenter of fieldCenters) {
        const dist = turf.distance(pCenter, fCenter);

        if (dist < 0.05) {
          // 50m
          skipped++;
          continue outer;
        }
      }

      if (outline.size >= 0.1) {
        result.push(outline);
      }
    }

    return result;
  } catch (e) {
    console.error(e);
    const result = [];
    for (const outline of outlines) {
      if (outline.size >= 0.3) {
        result.push(outline);
      }
    }

    return result;
  }
}

export function getFilteredOutlinesSoftWFS(
  data,
  existingFields,
  progressCallback = (progress, progressMax, skipped) => {}
) {
  let progress = 0,
    skipped = 0,
    progressMax = data.length;

  try {
    const result = [];
    // Figure out which outlines represents fields which are already added
    const fieldPolygons = existingFields.map(fieldToTurfPolygon);
    const fieldCenters = fieldPolygons.map((p) => turf.centerOfMass(p));

    outer: for (const entry of data.features) {
      const coordinates = entry.geometry.coordinates[0].map((obj) => {
        return { lat: obj[1], lng: obj[0] };
      });

      const outline = new Outline();
      const properties = entry.properties;
      outline.size = properties.areal;
      outline.coordinates = coordinates;

      progressCallback(progress, progressMax, skipped);
      progress++;

      const p = turf.polygon(outline.coordinates);
      const pCenter = turf.centerOfMass(p);

      for (const fCenter of fieldCenters) {
        const dist = turf.distance(pCenter, fCenter);

        if (dist < 0.05) {
          // 50m
          skipped++;
          continue outer;
        }
      }

      if (outline.size >= 0.3) {
        result.push(outline);
      }
    }

    return result;
  } catch (e) {
    console.error(e);
    const result = [];
    for (const outline of outlines) {
      if (outline.size >= 0.3) {
        result.push(outline);
      }
    }

    return result;
  }
}
